import { ActorRefFrom, assign, fromPromise, setup } from 'xstate';
import { confirmationView } from '~/features/confirmation/confirmationView.machine';
import { providerAssociationMachine } from '~/features/employer/provider-associations/providerAssociation.machine';
import { marketContractsMachine } from '~/features/market/contracts/marketContracts.machine';
import { employerFacilitiesMachine } from '~/features/market/facility/employerFacilities.machine';
import { listingsSearchMachine } from '~/features/market/listing-search/listingsSearch.machine';
import { jobListingMachine } from '~/features/market/listings/job-listing/jobListing.machine';
import { msaasListingsMachine } from '~/features/market/listings/msaas-listing/msaasListing.machine';
import { negotiationMachine } from '~/features/market/negotiations/negotiation.machine';
import { employerInfoMachine } from '~/features/market/organization/employer-info/employerInfo.machine';
import { MARKET_PAGE_KEYS } from '~/routing';
import { employerService } from '~/services/employer/employer.service.machine';
import { providerService } from '~/services/provider/provider.service.machine';
import { EmployerUser } from '~/types/hlx/employerUser';
import { Role } from '../user/types';
import {
  EMPLOYER_FACILITIES_STATE,
  EMPLOYER_INFO_STATE,
  EMPLOYER_SERVICE,
  JOB_LISTING_STATE,
  MARKET_CONTRACTS_STATE,
  MARKET_STATE,
  MSAAS_LISTINGS_SEARCH_STATE,
  MSAAS_LISTINGS_STATE,
  MSAAS_OFFERING_SEARCH_STATE,
  NEGOTIATION_STATE,
  PROVIDER_ASSOCIATION_STATE,
  PROVIDER_SERVICE,
} from '../xstateSystem';
import { Events } from './events';
import { MarketStateContext } from './types';
import { assignCurrentPage, createPageGuard } from './utils';

//  but moved to the proper location, and having a better name
//  however; its not linked into any of the *.tsx feature views/forms/etc

/**
 * This accountState machine logic handles the logged-in employer user state, often by delegating isolated
 * portions of the user-flow out to sub-machines (spawned actors).
 */
export const marketState = setup({
  types: {
    context: {} as MarketStateContext,
    events: {} as Events,
    input: {} as Partial<MarketStateContext>,
  },
  guards: {
    isEmployer: ({ context }) => context.roleCode === 'EMPLOYER',
    isProvider: ({ context }) => context.roleCode === 'PROVIDER',
  },
  actors: {
    // Add employer-specific machines here
    confirmationRef: confirmationView,
    marketContractsRef: marketContractsMachine,
    employerFacilitiesRef: employerFacilitiesMachine,
    employerInfoRef: employerInfoMachine,
    msaasListingsRef: msaasListingsMachine,
    jobListingRef: jobListingMachine,
    negotiationRef: negotiationMachine,
    listingsSearchRef: listingsSearchMachine,
    providerAssociationRef: providerAssociationMachine,
    employerService: employerService,
    providerService: providerService,
    waitForEmployer: fromPromise(
      async ({
        input,
      }: {
        input: { employerService: ActorRefFrom<typeof employerService> | undefined };
      }): Promise<EmployerUser> => {
        if (!input.employerService) {
          return Promise.reject('employerService is required in waitForEmployer');
        }
        const { employerService } = input;

        const snapshot = employerService.getSnapshot();
        if (snapshot.matches('$_SUCCESS') && snapshot.context.employerUser) {
          return snapshot.context.employerUser;
        }

        return new Promise((resolve, reject) => {
          const sub = employerService.subscribe((snapshot) => {
            if (snapshot.matches('$_SUCCESS') && snapshot.context.employerUser) {
              sub.unsubscribe();
              resolve(snapshot.context.employerUser);
            } else if (snapshot.matches('$_ERROR')) {
              sub.unsubscribe();
              reject(snapshot.context.error);
            }
          });
        });
      }
    ),
    waitForProvider: fromPromise(
      async ({
        input,
      }: {
        input: { providerService: ActorRefFrom<typeof providerService> | undefined };
      }) => {
        if (!input.providerService) {
          return Promise.reject('providerService is required in waitForProvider');
        }
        const { providerService } = input;

        const snapshot = providerService.getSnapshot();
        if (snapshot.matches('$_SUCCESS') && snapshot.context.provider) {
          return snapshot.context.provider;
        }
      }
    ),
  },
}).createMachine({
  context: ({ input, spawn, self }) => ({
    isProvider: input.isProvider || false,
    employerService:
      input?.employerService ||
      (input.roleCode === 'EMPLOYER'
        ? spawn('employerService', { id: EMPLOYER_SERVICE, systemId: EMPLOYER_SERVICE, input: {} })
        : undefined),
    providerService:
      input?.providerService ||
      (input.roleCode === 'PROVIDER'
        ? spawn('providerService', {
            id: `${PROVIDER_SERVICE}-market`,
            systemId: `${PROVIDER_SERVICE}-market`,
            input: { providerId: input.providerId },
          })
        : undefined),
    userInfoId: input.userInfoId || 0,
    roleCode: input.roleCode || ('EMPLOYER' as Role),
    providerId: undefined,
    provider: null,
    employerUser: undefined,
    // [MCV-1537] Fallback must be a bare page key (matches what createPageGuard
    // compares against), not a full path like EMPLOYER_AUTH_HOME ('/hlx/dashboard') —
    // the same normalization the boot self-sends below already use.
    currentPage: window.location.pathname.split('/').pop() || MARKET_PAGE_KEYS.dashboard,
    confirmationRef: spawn('confirmationRef', {
      id: 'marketConfirmationState',
      input: {},
    }),
    marketContractsRef: spawn('marketContractsRef', {
      id: MARKET_CONTRACTS_STATE,
      input: undefined,
    }),
    employerFacilitiesRef: spawn('employerFacilitiesRef', {
      id: EMPLOYER_FACILITIES_STATE,
      systemId: EMPLOYER_FACILITIES_STATE,
      input: undefined,
    }),
    employerInfoRef: spawn('employerInfoRef', {
      id: EMPLOYER_INFO_STATE,
      systemId: EMPLOYER_INFO_STATE,
      input: { market: self },
    }),
    msaasListingsRef: spawn('msaasListingsRef', {
      id: MSAAS_LISTINGS_STATE,
      systemId: MSAAS_LISTINGS_STATE,
      input: undefined,
    }),
    jobListingRef: spawn('jobListingRef', {
      id: JOB_LISTING_STATE,
      systemId: JOB_LISTING_STATE,
      input: undefined,
    }),
    msaasListingsSearchRef: spawn('listingsSearchRef', {
      id: MSAAS_LISTINGS_SEARCH_STATE,
      systemId: MSAAS_LISTINGS_SEARCH_STATE,
      input: undefined,
    }),
    msaasOfferingSearchRef: spawn('listingsSearchRef', {
      id: MSAAS_OFFERING_SEARCH_STATE,
      systemId: MSAAS_OFFERING_SEARCH_STATE,
      input: { listingSide: 'OFFERING' },
    }),
    negotiationRef: spawn('negotiationRef', {
      id: NEGOTIATION_STATE,
      systemId: NEGOTIATION_STATE,
      input: undefined,
    }),
    providerAssociationRef: (() => {
      const existing = self.system.get(PROVIDER_ASSOCIATION_STATE);

      if (input.isProvider) {
        return null;
      }
      if (existing) {
        return existing;
      }
      return spawn('providerAssociationRef', {
        id: 'providerAssociationState',
        systemId: PROVIDER_ASSOCIATION_STATE,
        input: undefined,
      });
    })(),
  }),
  id: MARKET_STATE,
  systemId: MARKET_STATE,
  initial: '$_ROUTING',
  on: {
    // Saving Employer Info renames the employer server-side; mirror it here so the sidebar
    // title updates without a reload.
    ON_EMPLOYER_NAME_UPDATED: {
      actions: [
        assign({
          employerUser: ({ context, event }) =>
            context.employerUser && {
              ...context.employerUser,
              employer: {
                ...context.employerUser.employer,
                employerName: event.payload.employerName,
              },
            },
        }),
        ({ context, event }) => context.employerService?.send(event),
      ],
    },
  },
  states: {
    $_ROUTING: {
      always: [
        { target: '$_EMPLOYER', guard: 'isEmployer' },
        { target: '$_PROVIDER', guard: 'isProvider' },
      ],
    },
    $_EMPLOYER: {
      initial: '$_LOAD_EMPLOYER',
      on: {
        ON_REQUEST_EMPLOYER_USER: {
          actions: (props) => {
            props.event.actor.send({
              type: 'EMPLOYER_USER_RESPONSE',
            });
          },
        },
        ON_SET_PAGE: [
          /* eslint-disable @typescript-eslint/no-explicit-any */
          {
            target: '.$_DASHBOARD_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.dashboard }) as any,
          },
          {
            target: '.$_CALENDAR_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.calendar }) as any,
          },
          {
            target: '.$_MSAAS_LISTINGS_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.msaasListings }) as any,
          },
          {
            target: '.$_JOB_LISTINGS_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.listings }) as any,
          },
          {
            target: '.$_LISTING_SEARCH_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.listingSearch }) as any,
          },
          {
            target: '.$_CONTRACTS_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.contracts }) as any,
          },
          {
            target: '.$_PROVIDER_ASSOCIATIONS_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.providerAssociations }) as any,
          },
          {
            target: '.$_EMPLOYER_INFO_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.employerInfo }) as any,
          },
          {
            target: '.$_FACILITIES_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.facilities }) as any,
          },
          {
            target: '.$_TEAM_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.team }) as any,
          },
          {
            target: '.$_JOB_OPPORTUNITIES_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.jobOpportunities }) as any,
          },
          {
            target: '.$_SERVICE_OPPORTUNITIES_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.serviceOpportunities }) as any,
          },
          {
            target: '.$_MY_SERVICES_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.myServices }) as any,
          },
          {
            target: '.$_B2B_OPPORTUNITIES_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.b2bOpportunities }) as any,
          },
          {
            // [MCV-1603] Terminal catch-all. The boot self-send below posts
            // window.location.pathname's last segment, and the guarded
            // branches above cover only the pages this machine loads data
            // for. Any other HlxLayout route (settings, billing, admin,
            // search, provider-view, impersonation, and listings on the
            // provider branch) matched nothing, so the event was dropped --
            // and because the $_LOAD_* invoke's onDone has actions but no
            // target, the actor never left $_LOAD_EMPLOYER/$_LOAD_PROVIDER.
            // useMarketPageSync's Guard 1 then read that as "still loading"
            // and swallowed every later bare-Link/back-forward navigation
            // (the MCV-1537 failure shape). This branch is unguarded and
            // last, so it cannot shadow the branches above; it just parks
            // the machine in a data-less view so the load completes.
            target: '.$_OTHER_VIEW',
            actions: assignCurrentPage as any,
          },
        ],
      },
      states: {
        $_LOAD_EMPLOYER: {
          invoke: {
            src: 'waitForEmployer',
            input: ({ context }: { context: MarketStateContext }) => ({
              employerService: context.employerService,
            }),
            onDone: {
              actions: [
                assign({
                  employerUser: ({ event }) => event.output,
                }),
                ({ context, event }) => {
                  const employerUser = event.output;

                  context.msaasListingsRef.send({
                    type: 'ON_SET_EMPLOYER_USER',
                    payload: { employerUser },
                  });
                  context.jobListingRef.send({
                    type: 'ON_SET_EMPLOYER_USER',
                    payload: { employerUser },
                  });
                  context.negotiationRef.send({
                    type: 'ON_SET_EMPLOYER_USER',
                    payload: { employerUser },
                  });
                  context.msaasListingsSearchRef.send({
                    type: 'ON_SET_EMPLOYER_USER',
                    payload: { employerUser },
                  });
                  context.msaasOfferingSearchRef.send({
                    type: 'ON_SET_EMPLOYER_USER',
                    payload: { employerUser },
                  });
                  context.marketContractsRef.send({
                    type: 'ON_LOAD',
                    payload: { employerUser },
                  });
                },
                ({ self }) =>
                  self.send({
                    type: 'ON_SET_PAGE',
                    payload: {
                      page: window.location.pathname.split('/').pop() || MARKET_PAGE_KEYS.dashboard,
                    },
                  }),
              ],
            },
            onError: '$_DASHBOARD_VIEW',
          },
        },
        $_DASHBOARD_VIEW: {},
        $_MSAAS_LISTINGS_VIEW: {
          entry: [
            ({ context }) => {
              if (!context.employerUser?.employerId) {
                return;
              }
              context.msaasListingsRef.send({
                type: 'ON_LOAD',
                payload: { employerUser: context.employerUser },
              });
            },
          ],
        },
        $_LISTING_SEARCH_VIEW: {
          entry: [
            ({ context }) => {
              if (!context.employerUser?.employerId) {
                return;
              }
              context.msaasListingsSearchRef.send({
                type: 'ON_ROUTE_ENTER',
                payload: { employerUser: context.employerUser },
              });
              context.msaasOfferingSearchRef.send({
                type: 'ON_ROUTE_ENTER',
                payload: { employerUser: context.employerUser },
              });
            },
          ],
        },
        $_CONTRACTS_VIEW: {
          entry: [
            ({ context }) =>
              context.marketContractsRef.send({
                type: 'ON_LOAD',
                payload: { employerUser: context.employerUser },
              }),
          ],
        },
        $_JOB_LISTINGS_VIEW: {
          entry: [
            ({ context }) => {
              if (!context.employerUser?.employerId) {
                return;
              }
              context.jobListingRef.send({
                type: 'ON_LOAD',
                payload: { employerUser: context.employerUser },
              });
            },
          ],
        },
        $_PROVIDER_ASSOCIATIONS_VIEW: {
          entry: [
            ({ context }) => {
              // Only load associations if employerUser is available
              // This handles the case where we navigate to this page before employer loading completes
              if (context.employerUser && context.providerAssociationRef) {
                context.providerAssociationRef.send({
                  type: 'ON_LOAD',
                  payload: { employerId: context.employerUser.employerId },
                });
              }
            },
          ],
        },
        $_EMPLOYER_INFO_VIEW: {
          entry: [
            ({ context }) => {
              if (!context.employerUser?.employerId) {
                return;
              }
              context.employerInfoRef.send({ type: 'ON_LOAD' });
            },
          ],
        },
        $_FACILITIES_VIEW: {
          entry: [
            ({ context }) => {
              if (!context.employerUser?.employerId) {
                return;
              }
              context.employerFacilitiesRef.send({ type: 'ON_LOAD' });
            },
          ],
        },
        $_TEAM_VIEW: {},
        $_CALENDAR_VIEW: {},
        $_JOB_OPPORTUNITIES_VIEW: {},
        $_SERVICE_OPPORTUNITIES_VIEW: {},
        $_MY_SERVICES_VIEW: {},
        $_B2B_OPPORTUNITIES_VIEW: {},
        $_OTHER_VIEW: {},
      },
    },
    $_PROVIDER: {
      initial: '$_LOAD_PROVIDER',
      on: {
        ON_REQUEST_PROVIDER: {
          actions: (props) => {
            props.event.actor.send({ type: 'PROVIDER_RESPONSE' });
          },
        },
        ON_SET_PAGE: [
          /* eslint-disable @typescript-eslint/no-explicit-any */
          {
            target: '.$_DASHBOARD_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.dashboard }) as any,
          },
          {
            target: '.$_CALENDAR_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.calendar }) as any,
          },
          {
            target: '.$_JOB_OPPORTUNITIES_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.jobOpportunities }) as any,
          },
          {
            target: '.$_SERVICE_OPPORTUNITIES_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.serviceOpportunities }) as any,
          },
          {
            target: '.$_MY_SERVICES_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.myServices }) as any,
          },
          {
            target: '.$_CONTRACTS_VIEW',
            actions: assignCurrentPage as any,
            guard: createPageGuard({ page: MARKET_PAGE_KEYS.contracts }) as any,
          },
          {
            // [MCV-1603] Terminal catch-all. The boot self-send below posts
            // window.location.pathname's last segment, and the guarded
            // branches above cover only the pages this machine loads data
            // for. Any other HlxLayout route (settings, billing, admin,
            // search, provider-view, impersonation, and listings on the
            // provider branch) matched nothing, so the event was dropped --
            // and because the $_LOAD_* invoke's onDone has actions but no
            // target, the actor never left $_LOAD_EMPLOYER/$_LOAD_PROVIDER.
            // useMarketPageSync's Guard 1 then read that as "still loading"
            // and swallowed every later bare-Link/back-forward navigation
            // (the MCV-1537 failure shape). This branch is unguarded and
            // last, so it cannot shadow the branches above; it just parks
            // the machine in a data-less view so the load completes.
            target: '.$_OTHER_VIEW',
            actions: assignCurrentPage as any,
          },
        ],
      },
      states: {
        $_DASHBOARD_VIEW: {},
        $_OTHER_VIEW: {},
        $_CALENDAR_VIEW: {},
        $_JOB_OPPORTUNITIES_VIEW: {},
        $_SERVICE_OPPORTUNITIES_VIEW: {},
        $_MY_SERVICES_VIEW: {},
        // [MCV-1727] Providers sign the contracts they are a party to (JOB: the
        // contracted provider; SERVICE: the listing provider). The contracts
        // machine loads the signed-in user's contracts server-side, so there is no
        // employer user to pass; without this state the page key fell through to
        // $_OTHER_VIEW and the list never got an ON_LOAD.
        $_CONTRACTS_VIEW: {
          entry: [
            ({ context }) =>
              context.marketContractsRef.send({
                type: 'ON_LOAD',
                payload: { employerUser: undefined },
              }),
          ],
        },
        $_LOAD_PROVIDER: {
          invoke: {
            src: 'waitForProvider',
            input: ({ context }: { context: MarketStateContext }) => ({
              providerService: context.providerService,
            }),
            onDone: {
              actions: [
                assign({
                  provider: ({ event }) => event.output ?? null,
                }),
                ({ context, event }) => {
                  const provider = event.output;
                  if (provider) {
                    context.negotiationRef.send({
                      type: 'ON_SET_PROVIDER',
                      payload: { provider },
                    });
                  }
                },
                ({ self }) =>
                  self.send({
                    type: 'ON_SET_PAGE',
                    payload: {
                      // [MCV-1537] Bare page key, not EMPLOYER_AUTH_HOME's full path —
                      // matches the employer boot self-send's fallback above.
                      page: window.location.pathname.split('/').pop() || MARKET_PAGE_KEYS.dashboard,
                    },
                  }),
              ],
            },
            onError: '$_DASHBOARD_VIEW',
          },
        },
      },
    },
  },
});
